import type en from '../en/common'
import type { Translation } from '../types'

/** Words and formats used across the dashboard */
const common: Translation<typeof en> = {
  "common.justNow": "à l’instant",
  "common.minutesAgo": "il y a {count} min",
  "common.hoursAgo": "il y a {count} h",
  "common.daysAgo": "il y a {count} j",
  "common.duration.minutes": { one: "{count} minute", other: "{count} minutes" },
  "common.duration.hours": { one: "{count} heure", other: "{count} heures" },
  "common.duration.days": { one: "{count} jour", other: "{count} jours" },
  "common.duration.weeks": { one: "{count} semaine", other: "{count} semaines" },
  "common.cancel": "Annuler",
  "common.confirm": "Confirmer",
  "common.continue": "Continuer",
  "common.working": "En cours",
  "common.loading": "Chargement…",
  "common.tryAgain": "Cela n’a pas fonctionné — veuillez réessayer",
  "common.tryAgainButton": "Réessayer",
  "common.retry": "Réessayer",
  "common.copy": "Copier",
  "common.download": "Télécharger",
  "common.saved": "Enregistré",
  "common.notSaved": "Non enregistré",
  "common.couldNotCopy": "Impossible de copier",
  "common.goBack": "Retour",
  "common.password": "Mot de passe",
  "common.emailAddress": "Adresse e-mail",
  "common.showPassword": "Afficher le mot de passe",
  "common.hidePassword": "Masquer le mot de passe",
  "common.signOut": "Se déconnecter",
  "common.notFoundTitle": "Page introuvable",
  "common.notFoundText": "La page que vous recherchez n’existe pas ou vous n’y avez pas accès.",
  "common.errorTitle": "Une erreur s’est produite",
  "common.errorUnexpected": "Une erreur inattendue s’est produite.",
  "common.loadFailed": "Impossible de charger les données.",
  "common.showing": "{start}–{end} sur {total}",
  "common.previousPage": "Page précédente",
  "common.nextPage": "Page suivante",
  "common.notifications": "Notifications",
  "common.dismiss": "Fermer",
  "common.refresh": "Actualiser",
  "common.viewAll": "Tout afficher",
  "common.view": "Afficher",
  "common.delete": "Supprimer",
  "common.close": "Fermer",
  "common.actions": "Actions",
  "common.status": "État",
  "common.name": "Nom",
  "common.description": "Description",
  "common.clients": "Clients",
  "common.devices": "Appareils",
  "common.active": "Actif",
  "common.disabled": "Désactivé",
  "common.saveFailed": "Échec de l’enregistrement",
  "common.internalError": "Erreur interne du serveur",
  "common.clientsCount": { one: "{count} client", other: "{count} clients" },
  "common.devicesCount": { one: "{count} appareil", other: "{count} appareils" },
  "common.onlineCount": "{count} en ligne",
  "common.critical": { one: "{count} critique", other: "{count} critiques" },
  "common.status.ONLINE": "EN LIGNE",
  "common.status.OFFLINE": "HORS LIGNE",
  "common.status.WARNING": "AVERTISSEMENT",
  "common.status.ERROR": "ERREUR",
  "common.status.PENDING": "EN ATTENTE",
  "common.status.PENDING_VPN": "EN ATTENTE DU VPN",
  "common.status.PROVISIONING": "EN PRÉPARATION",
  "common.status.ACTIVE": "ACTIF",
  "common.status.INACTIVE": "INACTIF",
  "common.status.COMPLETED": "TERMINÉ",
  "common.status.RUNNING": "EN COURS",
  "common.status.FAILED": "ÉCHEC",
  "common.status.EXPIRED": "EXPIRÉ",
  "common.status.SUSPENDED": "SUSPENDU",
  "common.status.DELETED": "SUPPRIMÉ",
  "common.status.FAILOVER": "BASCULEMENT",
  "common.status.APPLIED": "APPLIQUÉ",
  "common.status.UNSUPPORTED": "NON PRIS EN CHARGE",
  "common.select": "Choisir…",
  "common.selectAll": "Tout sélectionner",
  "common.deselectAll": "Tout désélectionner",
  "common.more": "+{count} de plus",
  "common.optional": "(facultatif)",
  "common.sitesCount": { one: "{count} site", other: "{count} sites" },
  "common.send": "Envoyer",
  "common.uptime.days": "{d} j {h} h",
  "common.uptime.hours": "{h} h {m} min",
  "common.uptime.minutes": "{m} min",
  "common.copied": "Copié",
  "common.remove": "Supprimer",
  "common.saving": "Enregistrement…",
  "common.username": "Nom d’utilisateur",
  "common.tryAgainMoment": "Réessayez dans un instant.",
  "common.duration.seconds": { one: "{count} seconde", other: "{count} secondes" },
  "common.status.PENDING_SETUP": "EN CONFIGURATION",
  "common.status.CANCELLED": "RÉSILIÉE",
  "common.languageName.en": "anglais",
  "common.languageName.nl": "néerlandais",
  "common.languageName.fr": "français",
  "common.languageName.de": "allemand",
  "common.languageName.es": "espagnol",
  "common.status.QUEUED": "GARDÉE POUR PLUS TARD",
  "common.status.DISCARDED": "ABANDONNÉE",
  "common.renewCert.title": "Renouveler le certificat VPN de {name} ?",
  "common.renewCert.titleMine": "Renouveler votre certificat VPN ?",
  "common.renewCert.stops": "Le certificat actuel cesse de fonctionner aussitôt : plus de connexion VPN tant que le nouveau fichier n’est pas installé.",
  "common.renewCert.remote": "Une unité ou un contrôleur joint uniquement par le VPN n’est alors plus joignable d’ici : installer le nouveau fichier peut demander quelqu’un sur place.",
  "common.renewCert.noWayBack": "C’est irréversible : l’ancien certificat ne peut plus être utilisé.",
  "common.renewCert.continue": "Compris, continuer",
  "common.renewCert.last": "Renouveler le certificat maintenant ? L’actuel cesse de fonctionner dès que vous cliquez.",
  "common.renewCert.renewNow": "Renouveler maintenant",
  "common.export.button": "Exporter",
  "common.export.title": "Exporter",
  "common.export.what": "Contenu",
  "common.export.summaryNone": "Tout ce qui est affiché",
  "common.export.rows": "Lignes",
  "common.export.thisPage": "Cette page ({n})",
  "common.export.allMatches": "Tous les résultats ({n})",
  "common.export.tooMany": "Plus de 10 000 lignes : exportez cette page, ou affinez les filtres",
  "common.export.parts": "Parties",
  "common.export.format": "Format",
  "common.export.xlsx": "Tableur (.xlsx)",
  "common.export.csv": "Tableur (CSV)",
  "common.export.pdf": "Rapport PDF",
  "common.export.md": "Consigne Markdown pour Claude Code",
  "common.export.go": "Exporter",
  "common.export.working": "Export en cours…",
  "common.export.failed": "L’export n’a pas abouti",
  "common.export.search": "recherche « {q} »",
  "common.export.sortedBy": "trié par {column}",
  "common.export.sortedByDesc": "trié par {column}, décroissant",
  "common.export.field": "Champ",
  "common.export.value": "Valeur",
  "common.export.deviceInfo": "Informations de l’appareil",
  "common.export.siteInfo": "Informations du site",
  "common.export.traffic": "Trafic",
  "common.export.cpu": "Processeur",
  "common.export.serial": "Numéro de série",
  "common.export.mac": "Adresse MAC",
  "common.export.level": "Niveau",
  "common.export.title_": "Titre",
  "common.export.message": "Message",
  "common.export.time": "Heure",
  "common.export.security": "Sécurité",
  "common.export.band": "Bande",
  "common.export.vlan": "VLAN",
  "common.export.ssid": "SSID",
  "common.export.enabled": "Activé",
  "common.export.clientCount": "Clients",
  "common.export.period": "période {period}",
  "common.export.range": "du {from} au {to}",
  "common.export.radios": "Radios",
  "common.export.modem": "Modem",
  "common.export.operator": "Opérateur",
  "common.export.network": "Type de réseau",
  "common.export.events": "Événements",
  "common.export.controller": "Contrôleur",
  "common.export.wired": "Filaire",
  "common.export.eventInfo": "Informations de l’événement",
  "common.export.online": "En ligne et hors ligne",
  "common.export.loads": "Sorties et retours",
  "common.export.customer": "Client",
  "common.export.resolvedAt": "Résolu",
  "common.export.loadedAt": "Chargé",
  "common.export.loadedBy": "Chargé par",
  "common.export.returnedAt": "Revenu",
  "common.export.returnedBy": "Rapporté par",
  "common.export.figures": "Chiffres",
  "common.export.critical": "Critiques",
  "common.export.vlans": "VLAN",
  "common.export.wifi": "Wi-Fi",
  "common.export.kind": "Type",
  "common.export.changedBy": "Modifié par",
  "common.export.changedAt": "Modifié le",
  "common.export.change": "Changement",
  "common.export.articleInfo": "Infos de l’article",
  "common.export.when": "Quand",
  "common.export.copied": "Copié et téléchargé",
  "common.export.downloadedOnly": "Téléchargé (la copie n’est pas autorisée ici)",
  "common.export.twoFactor": "Connexion à deux facteurs",
  "common.export.systemRole": "Rôle système",
  "common.export.type": "Type",
  "common.export.deviceFamily": "Famille d’appareils",
  "common.export.origin": "Origine",
  "common.export.isDefault": "Par défaut",
  "common.export.madeBy": "Créé par",
  "common.export.created": "Créé le",
  "common.export.applied": "Nombre d’applications",
  "common.export.watches": "Surveille",
  "common.export.channels": "Canaux",
  "common.export.mutedDevices": "Appareils en sourdine",
  "common.export.who": "Qui",
  "common.export.action": "Action",
  "common.export.resource": "Ressource",
  "common.export.health": "État",
  "common.export.goesTo": "Mène à",
  "common.export.checkedAt": "Vérifié le",
  "common.export.times": "Fois rencontré",
  "common.export.exampleSite": "Site d’exemple",
  "common.export.noLimit": "Sans limite",
  "common.export.lastSeen": "Vu pour la dernière fois",
  "common.export.simSlot": "Emplacement SIM",
  "common.export.pinTries": "Essais PIN restants",
  "common.export.pukTries": "Essais PUK restants",
  "common.export.pinUsed": "PIN utilisé",
  "common.export.storedPin": "Le PIN enregistré du routeur correspond",
  "common.export.readAt": "Lu le",
  "common.export.sims": "Emplacements SIM",
  "common.export.latency": "Latence (ms)",
  "common.export.loss": "Perte (%)",
  "common.export.peer": "Pair",
  "common.export.signal": "{name} (dBm)",
}

export default common
